import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import { LEAD_TYPES } from "../services/leadEmail";
import { submitLegacyLead } from "../services/legacyLeadSubmission";

const CAMPAIGN_SLUG = "ranger-q3-2026-campaign";

function CountUpStat({ title, footnoteId, value, suffix = "" }) {
  const [displayValue, setDisplayValue] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const statRef = useRef(null);

  useEffect(() => {
    const currentRef = statRef.current;

    if (!currentRef) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasStarted(true);
          observer.disconnect();
        }
      },
      { threshold: 0.45 },
    );

    observer.observe(currentRef);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!hasStarted) {
      return undefined;
    }

    let animationFrame;
    const duration = 1200;
    const startTime = performance.now();

    const tick = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const easedProgress = 1 - Math.pow(1 - progress, 3);

      setDisplayValue(Math.round(value * easedProgress));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(tick);
      }
    };

    animationFrame = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(animationFrame);
  }, [hasStarted, value]);

  return (
    <div
      ref={statRef}
      className="flex min-h-[190px] flex-col justify-between p-8 text-center text-white bg-fordBlue rounded-2xl"
    >
      <p className="text-sm tracking-[0.18em] uppercase">
        {title}{" "}
        <a href={`#${footnoteId}`}>
          <sup>({footnoteId.replace("ranger-footnote-", "")})</sup>
        </a>
      </p>
      <p className="text-5xl leading-none font-fordBold">
        <span>{displayValue.toLocaleString("bg-BG")}</span>{" "}
        <span className="text-2xl align-baseline font-fordMedium">
          {suffix}
        </span>
      </p>
    </div>
  );
}

function RangerQ32026() {
  const navigate = useNavigate();
  const form = useForm();
  const { register, handleSubmit, formState } = form;
  const { errors } = formState;

  const submitRangerCampaign = async (data) => {
    try {
      await submitLegacyLead({
        collectionName: CAMPAIGN_SLUG,
        formData: data,
        firestoreData: {
          name: data.name ?? "",
          email: data.email ?? "",
          phone: data.phone ?? "",
          dealership: data.dealership ?? "",
          text: "",
          model: "Ranger",
        },
        leadPayload: {
          type: LEAD_TYPES.CAMPAIGN_OFFER,
          vehicle: {
            model: "Ranger",
          },
          campaign: {
            slug: CAMPAIGN_SLUG,
          },
          title: "ranger-q3-2026-campaign",
          message: "Запитване за Ranger кампания Q3 2026",
        },
      });

      navigate("/thank-you-offer");
    } catch (error) {
      console.error("Грешка при запис в Firebase:", error);
    }
  };

  const scrollToForm = () => {
    const formSection = document.getElementById("rangerQ3Form");
    if (formSection) {
      formSection.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  useEffect(() => {
    if (window.location.hash !== "#rangerQ3Form") {
      return undefined;
    }

    const scrollTimeout = window.setTimeout(scrollToForm, 100);

    return () => window.clearTimeout(scrollTimeout);
  }, []);

  return (
    <main className="w-full min-h-screen text-fordBlue font-fordAntennaRegular">
      <section className="relative overflow-hidden text-white bg-fordBlue">
        <picture>
          <source
            media="(max-width: 768px)"
            srcSet="/images/ranger-q3-2026/ranger-hero-mobile.png"
          />
          <img
            src="/images/ranger-q3-2026/ranger-hero-desktop.png"
            alt="Ford Ranger campaign"
            className="block w-full h-auto"
          />
        </picture>
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/25 to-transparent" />
        <div className="absolute bottom-8 left-[5%] top-8 z-10 flex max-w-3xl flex-col justify-between">
          <div>
            <p className="lg:text-lg tracking-[0.22em] uppercase font-fordMedium">
              Ford Ranger
            </p>
            <h1 className="text-2xl uppercase lg:text-4xl font-fordBlack">
              На специална цена и с ниска първоначална вноска
            </h1>
            <p className="text-2xl font-fordMedium lg:mt-2">
              от 3 299 € без ДДС*
            </p>
          </div>
          <div>
            <button
              type="button"
              onClick={scrollToForm}
              className="px-8 py-3 mt-8 text-sm tracking-wider uppercase border border-white rounded-full hover:bg-white hover:text-fordBlue"
            >
              Поискай оферта
            </button>
          </div>
        </div>
      </section>
      <button
        type="button"
        onClick={scrollToForm}
        className="fixed right-0 z-40 hidden origin-bottom-right -translate-y-1/2 rotate-[-90deg] rounded-t-2xl border border-fordBlue bg-fordBlue px-6 pt-2.5 pb-2 text-sm tracking-wide text-white uppercase shadow-lg transition-colors top-1/2 hover:bg-white hover:text-fordBlue lg:block"
      >
        Поискай оферта
      </button>

      <section className="max-w-5xl px-6 py-12 mx-auto text-center lg:py-16">
        <p className="text-xl leading-8 lg:text-2xl">
          С повишена товароносимост, водеща в своя клас теглителна сила,
          впечатляваща проходимост и опции за задвижване. Ето, защо изцяло
          новият Ford Ranger продължава да бъде най-продаваният пикап в Европа.
        </p>
      </section>

      <section className="grid max-w-6xl grid-cols-1 gap-4 px-4 pb-12 mx-auto md:grid-cols-3 lg:pb-24">
        <CountUpStat
          title="Теглителна способност"
          footnoteId="ranger-footnote-1"
          value={3500}
          suffix="кг"
        />
        <CountUpStat
          title="Носеща способност"
          footnoteId="ranger-footnote-2"
          value={1090}
          suffix="кг"
        />
        <CountUpStat
          title="Обем на товарното отделение"
          footnoteId="ranger-footnote-3"
          value={1232}
          suffix="л"
        />
      </section>

      <section className="grid grid-cols-1 gap-0 lg:my-20 lg:grid-cols-2">
        <div className="min-h-[360px] bg-gray-100">
          <img
            src="/images/ranger-q3-2026/connectivity.jpg"
            alt="Ford Ranger connectivity"
            className="object-cover w-full h-full"
          />
        </div>
        <div className="flex flex-col justify-center p-8 lg:p-16">
          <h2 className="text-4xl uppercase font-fordBlack">
            Свързаност и технологии
          </h2>
          <p className="mt-6 text-lg leading-8">
            Оборудван с арсенал от най-съвременни технологии за свързаност и
            техническа поддръжка. От подобрен гласов контрол, навигация,
            свързаност и Ford SYNC 4 до безжично зареждане на устройства или
            осигуряване на захранване за вашите електрически инструменти -
            изцяло новият Ranger е перфектният модерен работен кон.
          </p>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-4 p-4 lg:my-20 lg:grid-cols-3">
        <article className="overflow-hidden bg-gray-100 rounded-2xl">
          <img
            src="/images/ranger-q3-2026/load-space.jpg"
            alt="Товарно пространство"
            className="object-cover w-full h-64"
          />
          <div className="p-8">
            <h3 className="text-3xl">Товарно пространство</h3>
            <p className="mt-4 leading-7">
              По-широкото товарно пространство позволява поставянето на
              стандартен европалет между колесните рамки. Дължината на товарното
              пространтсво е 1 544 мм. Новото покритие осигурява по-добра защита
              на товарната повърхност.
            </p>
          </div>
        </article>

        <article className="overflow-hidden bg-gray-100 rounded-2xl">
          <img
            src="/images/ranger-q3-2026/tailgate.jpg"
            alt="Врата на багажното отделение"
            className="object-cover w-full h-64"
          />
          <div className="p-8">
            <h3 className="text-3xl">Врата на багажното отделение</h3>
            <p className="mt-4 leading-7">
              Новите задни врати се отварят по-лесно и могат да ви служат и като
              работна маса. Те разполагат с вградени джобове за скоби и държачи
              за чаши.
            </p>
          </div>
        </article>

        <article className="overflow-hidden bg-gray-100 rounded-2xl">
          <img
            src="/images/ranger-q3-2026/cargo-equipment.jpg"
            alt="Оборудване на товарното отделение"
            className="object-cover w-full h-64"
          />
          <div className="p-8">
            <h3 className="text-3xl">Оборудване на товарното отделение</h3>
            <p className="mt-4 leading-7">
              Товарното отделение осигурява безопасно и надеждно превозване на
              вашия товар. В помощ ще ви бъдат вътрешните релси, пружинните
              скоби и точките за закрепване, които отговарят на стандарта DIN и
              предлагат разнообразни възможности за закрепване на товара.
            </p>
          </div>
        </article>
      </section>

      <section className="grid grid-cols-1 gap-4 p-4 lg:my-20 lg:grid-cols-2">
        <article className="overflow-hidden text-white bg-fordBlue rounded-2xl">
          <img
            src="/images/ranger-q3-2026/terrain.jpg"
            alt="Ford Ranger terrain"
            className="object-cover w-full h-96"
          />
          <div className="p-8 lg:p-12">
            <h3 className="text-3xl">Покорявайте всеки терен</h3>
            <p className="mt-4 leading-8">
              Където и да карате, със системата за управление на терена ще
              можете да настроите автомобила си точно според текущите условия. С
              помощта на въртящия се бутон лесно превключвайте между избраните
              режими на шофиране.*
            </p>
          </div>
        </article>
        <article className="overflow-hidden text-white bg-fordBlue rounded-2xl">
          <img
            src="/images/ranger-q3-2026/4x4.jpg"
            alt="Ford Ranger 4x4"
            className="object-cover w-full h-96"
          />
          <div className="p-8 lg:p-12">
            <h3 className="text-3xl">4x4</h3>
            <p className="mt-4 leading-8">
              Карайте уверено при всякакви условия. Когато пожелаете да се
              отклоните от пътя или условията на пътя се влошат, можете
              мигновено да превключите между задвижване на две и четири колела.
              За още по-добро сцепление се предлага опционално и блокаж на
              задния диференциал, който е стандартно оборудване при нивото
              Wildtrak.
            </p>
          </div>
        </article>
      </section>

      <section className="relative mt-8 text-white bg-fordBlue lg:my-20">
        <picture>
          <source
            media="(max-width: 768px)"
            srcSet="/images/ranger-q3-2026/pickup-award-mobile.png"
          />
          <img
            src="/images/ranger-q3-2026/pickup-award.jpg"
            alt="Ford Ranger международен пикап на 2026 г."
            className="object-contain w-full min-h-[475px] bg-fordBlue lg:min-h-[640px] lg:object-cover"
          />
        </picture>
        <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/75 via-black/45 to-transparent" />
        <div className="absolute left-[5%] bottom-4 lg:bottom-12 max-w-2xl">
          <h2 className="text-xl uppercase lg:text-4xl font-fordBlack">
            Международен пикап на 2026 г.
          </h2>
          <p className="lg:mt-6 lg:text-lg lg:leading-8">
            Шампионът в тежка категория се завръща... Изцяло новият Ranger бе
            отличен с наградата „Международен пикап на 2026 г.“. Това е вече
            четвъртият път поред, в който Ford Ranger печели тази престижна
            награда. Изберете победителя за вашата компания.
          </p>
        </div>
      </section>

      <section className="flex flex-col items-center gap-4 px-4 py-12 lg:py-20 lg:flex-row lg:justify-center">
        <a
          href="https://ford.bg/commercial_cars/new-ranger"
          target="_blank"
          rel="noreferrer"
        >
          <button className="px-12 py-3 text-white border rounded-full bg-fordBlue border-fordBlue hover:bg-white hover:text-fordBlue">
            Научи повече
          </button>
        </a>
        <a href="https://sale.ford.bg/?model=Ranger">
          <button className="px-12 py-3 border rounded-full border-fordBlue text-fordBlue hover:bg-fordBlue hover:text-white">
            Налични автомобили
          </button>
        </a>
      </section>

      <motion.section
        id="rangerQ3Form"
        className="w-full max-w-5xl px-4 py-12 mx-auto"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.7 }}
      >
        <form
          onSubmit={handleSubmit(submitRangerCampaign)}
          className="px-6 py-10 bg-white border border-gray-200 shadow-sm rounded-2xl lg:px-16"
          noValidate
        >
          <h2 className="text-3xl tracking-wider text-center uppercase text-fordBlue">
            Поискай оферта
          </h2>

          <div className="mt-8">
            <label htmlFor="name" className="text-gray-500">
              Име
            </label>
            <input
              type="text"
              id="name"
              name="name"
              {...register("name", {
                required: {
                  value: true,
                  message: "Моля, въведете Вашето име",
                },
              })}
            />
            <p className="text-red-500">{errors.name?.message}</p>
          </div>

          <div className="mt-4">
            <label htmlFor="email" className="text-gray-500">
              Имейл
            </label>
            <input
              type="email"
              id="email"
              name="email"
              {...register("email", {
                required: {
                  value: true,
                  message: "Моля, въведете имейл-адрес",
                },
                pattern: {
                  value: /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/,
                  message: "Невалиден имейл-адрес",
                },
              })}
            />
            <p className="text-red-500">{errors.email?.message}</p>
          </div>

          <div className="mt-4">
            <label htmlFor="phone" className="text-gray-500">
              Телефон
            </label>
            <input
              type="text"
              id="phone"
              name="phone"
              {...register("phone", {
                required: {
                  value: true,
                  message: "Моля, въведете телефонен номер",
                },
                pattern: {
                  value:
                    /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/,
                  message: "Невалиден телефонен номер",
                },
              })}
            />
            <p className="text-red-500">{errors.phone?.message}</p>
          </div>

          <div className="mt-4">
            <label htmlFor="dealership" className="text-gray-500">
              Предпочитано представителство
            </label>
            <select
              name="dealership"
              id="dealership"
              {...register("dealership", {
                required: {
                  value: true,
                  message: "Моля, изберете представителство",
                },
              })}
            >
              <option value="" disabled>
                Моля, изберете
              </option>
              <option value="liulin2@motopfohe.bg">София Люлин</option>
              <option value="mladost@motopfohe.bg">София Младост</option>
              <option value="blagoevgrad@motopfohe.bg">Благоевград</option>
              <option value="burgas@motopfohe.bg">Бургас</option>
              <option value="varna@motopfohe.bg">Варна</option>
              <option value="velikotarnovo@motopfohe.bg">Велико Търново</option>
              <option value="gabrovo@motopfohe.bg">Габрово</option>
              <option value="dobrich@motopfohe.bg">Добрич</option>
              <option value="montana@motopfohe.bg">Монтана</option>
              <option value="pleven@motopfohe.bg">Плевен</option>
              <option value="plovdiv@motopfohe.bg">Пловдив</option>
              <option value="russe@motopfohe.bg">Русе</option>
              <option value="starazagora@motopfohe.bg">Стара Загора</option>
              <option value="smolyan@motopfohe.bg">Смолян</option>
              <option value="forsh@motopfohe.bg">Шумен</option>
            </select>
            <p className="text-red-500">{errors.dealership?.message}</p>
          </div>

          <button
            type="submit"
            className="px-16 py-2 mx-auto mt-8 text-white border rounded-full bg-fordBlue"
          >
            Изпрати
          </button>
        </form>
      </motion.section>

      <section className="max-w-5xl px-4 pb-12 mx-auto text-sm leading-6 text-gray-500">
        <p>
          * Посочената първоначална вноска е без ДДС и е валидна при одобрение и
          сключване на нов договор за лизинг за Ford Ranger 2.0 TDCi 170 к.с.
          XLT. Не включва приложимите такси, застраховки, разходи за регистрация
          и други съпътстващи разходи. Възможност за ползване на данъчен кредит
          при наличие на законовите предпоставки. Предложението е валидно до
          изчерпване на наличностите. Изображението е илюстративно.
        </p>
        <p>
          [1] Максималната допустима теглителна сила варира в зависимост от
          товара, конфигурацията на автомобила, допълнителното оборудване и броя
          на пътниците.
        </p>
        <p>
          [2]Максималният брутен товарен капацитет се отнася за модела Ranger
          Double Cab. Товарът и товарният капацитет са ограничени от теглото и
          разпределението на теглото. Максималната допустима товароносимост
          варира и зависи от допълнителното оборудване и конфигурацията на
          автомобила.
        </p>
        <p>
          [3]Максималният обем на товарното пространство се отнася за модела
          Ranger Double Cab.
        </p>
      </section>
    </main>
  );
}

export default RangerQ32026;
